import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Alert, Col, Form, message, Row } from 'antd';
import config from 'config';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import {
  useUpdateOwnerInfoMutation,
  useGetOwnerQuery,
  UserOutput,
  OwnerOutput,
  PermissionsEnum,
  UserRole,
} from 'gql/types';
import { Button, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { getCachedOwners } from 'modules/donor/utils/donor-cache';
import { GET_OWNER_QUERY } from 'modules/owner/graphql/queries';
import ArchivingButton from 'modules/user/components/ArchivingButton';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { apolloClient } from 'providers/graphql';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AdditionalContactsSection from './AdditionalContactsSection';
import { DeleteOwnerButton } from './DeleteOwnerButton';
import DonorsTable from './DonorsTable';
import PersonalInfoSection from './PersonalInfoSection';
import { editOwnerFormSchema, EditOwnerFormSchemaType } from './schema';
import styles from './styles.module.scss';

import { getDefaultParamsOwner } from '../EditProfileForm/defaultParams';

type PropTypes = {
  ownerId?: string | number;
};

const { UpdateOwnersData, ArchiveOwners } = PermissionsEnum;

const EditOwnerProfileForm: FC<PropTypes> = ({ ownerId }) => {
  const { t } = useTranslation('owner.EditOwnerProfileForm');

  const currentUser = useCurrentUser();
  const canUpdateOwners = currentUser.isCan(UpdateOwnersData);
  const canArchiveOwners = currentUser.isCan(ArchiveOwners);
  const canDeleteOwner = currentUser.data?.role.value === UserRole.Admin;

  const [formEnabled, setFormEnabled] = useState<boolean>(false);

  const navigate = useNavigate();
  const isOffline = !navigator.onLine;

  const [editOwner, editOwnerMutation] = useUpdateOwnerInfoMutation({
    refetchQueries: [
      {
        query: GET_OWNER_QUERY,
        variables: {
          input: { id: String(ownerId) },
        },
      },
    ],
    update: (cache) => {
      cache.evict({ id: `OwnerOutput:${ownerId}` });
    },
    onCompleted: (data) => {
      if (data.updateOwnerInfo.email !== ownerQuery.data?.getOwner?.email) {
        message.success(t('letterHasBeenSentToNewEmail'));
      } else {
        message.success(t('successfullySaved'));
      }
    },
    onError: (error) => message.error(error.message),
  });

  const editOwnerForm = useForm<EditOwnerFormSchemaType>({
    resolver: yupResolver(editOwnerFormSchema),
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
  } = editOwnerForm;

  const resetForm = (data?: OwnerOutput) => {
    reset(getDefaultParamsOwner(data));
  };

  const ownerQuery = useGetOwnerQuery({
    variables: {
      input: { id: String(ownerId) },
    },
    fetchPolicy: 'cache-first',
    onError: (error) => {
      if (isOffline) {
        return;
      }
      message.error(error.message);
      if (error.message === 'Not Found') {
        navigate(Routes.NotFound);
        return;
      }
      navigate(Routes.Owners);
    },
  });

  useEffect(() => {
    if (ownerQuery.data) {
      resetForm(ownerQuery.data.getOwner as OwnerOutput);
    }
  }, [ownerQuery.data]);

  useEffect(() => {
    if (!ownerId || !isOffline) {
      return;
    }

    const cachedOwner = getCachedOwners()[ownerId];

    if (cachedOwner) {
      apolloClient.writeQuery({
        query: GET_OWNER_QUERY,
        variables: { input: { id: String(ownerId) } },
        data: { getOwner: cachedOwner },
      });
      reset(getDefaultParamsOwner(cachedOwner));
      message.info('Offline owner data from cache');
    } else {
      message.error('There is no stored data for the owner');
      navigate(Routes.Owners);
    }
    return;
  }, [ownerId, isOffline]);

  const owner = ownerQuery.data?.getOwner as OwnerOutput | undefined;

  const submitForm = async (data: EditOwnerFormSchemaType) => {
    const { contact, personalInfo } = data;

    const personalInfoEntries = Object.entries(personalInfo);
    const transformedPersonalInfo = personalInfoEntries.reduce(
      (transformed, entry) => {
        return { ...transformed, [entry[0]]: entry[1] || null };
      },
      {}
    );
    const personalInfoState = editOwnerForm.getFieldState('personalInfo');
    const contactState = editOwnerForm.getFieldState('contact');

    const isAnySectionDirty = personalInfoState.isDirty || contactState.isDirty;
    if (!isAnySectionDirty) {
      setFormEnabled(!formEnabled);
      return;
    }

    editOwner({
      variables: {
        input: {
          id: ownerQuery.data!.getOwner.id,
          ...(transformedPersonalInfo as Required<typeof personalInfo>),
          additionalContact: {
            ...contact,
            id: ownerQuery?.data?.getOwner?.contacts?.at(0)?.id,
          },
        },
      },
    });
    setFormEnabled(!formEnabled);
  };

  const saveHandler = handleSubmit(submitForm);

  const handleSave = async () => {
    const formData = editOwnerForm.getValues();
    await submitForm(formData);
  };

  const isArchived = ownerQuery?.data?.getOwner?.isArchived;
  const isOwnerArchived = isArchived === undefined ? true : isArchived;

  const deletedAt = ownerQuery.data?.getOwner.deletedAt as Date | null;
  const formattedDate =
    deletedAt && format(new Date(deletedAt), config.DATE_FORMATS.DATE_AND_TIME);

  const checkIfSaveIsDisabled = () => {
    const isFormValid = editOwnerForm.formState.isValid;
    const isDirty = editOwnerForm.formState.isDirty;

    return !(isFormValid && isDirty && !isOwnerArchived);
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  const isLoading = editOwnerMutation.loading || ownerQuery.loading;

  return (
    <>
      <Spin spinning={isLoading}>
        {canUpdateOwners && !deletedAt && (
          <Row className={styles.topFormButtonsContainer}>
            <Col span={24}>
              <Row gutter={[10, 10]} justify="space-between">
                <Col>
                  {canDeleteOwner && <DeleteOwnerButton ownerId={owner?.id} />}
                </Col>
                <Col flex={1} />
                {canArchiveOwners && owner && (
                  <Col>
                    <ArchivingButton user={owner} />
                  </Col>
                )}
                <Col>
                  <Button
                    onClick={() => {
                      resetForm(owner);
                      setFormEnabled(false);
                    }}
                    disabled={!formEnabled}
                  >
                    {t('close')}
                  </Button>
                </Col>
                <Col>
                  {formEnabled && (
                    <Button
                      type="primary"
                      disabled={checkIfSaveIsDisabled()}
                      onClick={saveHandler}
                    >
                      {t('save')}
                    </Button>
                  )}

                  {!formEnabled && (
                    <Button
                      type="primary"
                      disabled={isOwnerArchived}
                      onClick={() => {
                        setFormEnabled(!formEnabled);
                        resetForm(owner);
                        editOwnerForm.trigger();
                      }}
                    >
                      {t('edit')}
                    </Button>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        {deletedAt && (
          <Alert
            message={t('userDeleted')}
            description={`${t('userDeletedAt')} ${formattedDate}`}
            type="error"
          />
        )}
        {ownerQuery?.data?.getOwner && (
          <>
            <FormProvider {...editOwnerForm}>
              <Form labelCol={{ span: 12 }} layout="vertical">
                <Row justify="space-between" gutter={[10, 0]}>
                  <Col sm={12} span={24}>
                    <PersonalInfoSection
                      fieldNamePrefix="personalInfo"
                      enabled={formEnabled}
                      owner={ownerQuery?.data?.getOwner as UserOutput}
                    />
                  </Col>
                  <Col sm={12} span={24}>
                    <AdditionalContactsSection
                      fieldNamePrefix="contact"
                      enabled={formEnabled}
                    />
                  </Col>
                </Row>
              </Form>
            </FormProvider>
            {owner && (
              <Row justify="space-between" gutter={[10, 0]}>
                <Col sm={12} span={24}>
                  <DonorsTable userId={owner.id} />
                </Col>
                <Col sm={12} span={24} />
              </Row>
            )}
          </>
        )}
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={isLoading}
      />
    </>
  );
};
export default EditOwnerProfileForm;
